/**
 * Copy do motivo de uma falha de envio da Meta — `{rotulo, explicacao, acao}` — em cascata:
 * **copy própria do código → texto da causa → genérico**.
 *
 * Fonte única pros três lugares do `MotivoDaFalha` (tela de disparo, menu da mensagem, ✕ do status).
 * Os códigos e o porquê de cada causa saem de `python-orchestrator/docs/auditoria-codigos-erro-meta-2026-10-18.md`;
 * os valores de causa, do contrato do épico `erros-meta` (ver sidecar).
 */

/** `MetaErrorCause` do back. No fio é string aberta: valor desconhecido cai no genérico (contrato `erros-meta`). */
export const CAUSAS = ['contato', 'conteudo', 'conta', 'limite', 'meta', 'integracao'] as const
export type CausaDoErroMeta = (typeof CAUSAS)[number]

export interface CopyDoErroMeta {
  rotulo: string
  explicacao: string
  acao?: string
}

export interface CopyDoCodigo extends CopyDoErroMeta {
  causa: CausaDoErroMeta
}

export interface MotivoDaFalha extends CopyDoErroMeta {
  causa?: CausaDoErroMeta
  codigo?: number
  /** De onde saiu o texto. Único leitor: `metaErrorLabel` (`dispatchOutcome.ts`) — `codigo` dá o rótulo puro,
   *  `causa` junta ` · <código>`, `generico` cai no `error_title`/`Erro N`. O `MotivoDaFalha` (componente) NÃO
   *  lê: recebe só `rotulo|explicacao|acao` e deduplica o texto cru contra o rótulo em `textosCrusDaMeta`. */
  origem: 'codigo' | 'causa' | 'generico'
}

export const COPY_GENERICA: CopyDoErroMeta = {
  rotulo: 'Falha no envio',
  explicacao: 'O WhatsApp não entregou esta mensagem.',
}

/** Códigos com texto próprio: os medidos na bancada + os de disparo/chat da auditoria. */
export const COPY_DO_CODIGO: Record<number, CopyDoCodigo> = {
  // — contato —
  131026: {
    causa: 'contato',
    rotulo: 'Mensagem não entregue ao número',
    explicacao: 'O número pode não ter WhatsApp, estar com o app desatualizado ou sem aceitar os termos de uso.',
    acao: 'Confirme o número com o contato por outro meio.',
  },
  131049: {
    causa: 'contato',
    rotulo: 'Contato no limite de marketing',
    explicacao: 'O WhatsApp limita o marketing que cada pessoa recebe, e este contato atingiu o dele. Não é falha do seu número nem da plataforma.',
    acao: 'Tente de novo após 24h ou use um template de utilidade.',
  },
  130472: {
    causa: 'contato',
    rotulo: 'Contato em teste do WhatsApp',
    explicacao: 'O WhatsApp incluiu este número num teste que bloqueia marketing. Reenviar não resolve.',
    acao: 'Peça que o contato mande uma mensagem e responda em até 24h.',
  },
  131050: {
    causa: 'contato',
    rotulo: 'Contato não aceita marketing',
    explicacao: 'O contato pediu para não receber marketing desta empresa.',
    acao: 'Não envie marketing para ele.',
  },
  130403: {
    causa: 'contato',
    rotulo: 'Contato bloqueado por vocês',
    explicacao: 'Este número da empresa bloqueou o contato.',
    acao: 'Desbloqueie o contato para enviar.',
  },
  131062: {
    causa: 'contato',
    rotulo: 'Contato sem telefone cadastrado',
    explicacao: 'O WhatsApp ainda não permite enviar só pelo ID do contato.',
    acao: 'Cadastre o telefone do contato.',
  },
  // — conteúdo —
  131047: {
    causa: 'conteudo',
    rotulo: 'Conversa fora das 24h',
    explicacao: 'Mensagem livre só pode ser enviada até 24h depois da última mensagem do contato.',
    acao: 'Envie um template.',
  },
  131051: {
    causa: 'conteudo',
    rotulo: 'Tipo de mensagem não aceito',
    explicacao: 'O WhatsApp não aceita este formato de mensagem.',
    acao: 'Envie em outro formato.',
  },
  131053: {
    causa: 'conteudo',
    rotulo: 'Arquivo não aceito',
    explicacao: 'O WhatsApp não conseguiu processar o arquivo, geralmente por formato não suportado.',
    acao: 'Envie o arquivo em outro formato.',
  },
  132000: {
    causa: 'conteudo',
    rotulo: 'Faltam variáveis no template',
    explicacao: 'O número de variáveis preenchidas não bate com o template.',
    acao: 'Preencha todas as variáveis e envie de novo.',
  },
  132001: {
    causa: 'conteudo',
    rotulo: 'Template indisponível',
    explicacao: 'O template não existe nesse idioma ou ainda não foi aprovado.',
    acao: 'Avise o administrador.',
  },
  132007: {
    causa: 'conteudo',
    rotulo: 'Template recusado por política',
    explicacao: 'O conteúdo do template viola uma regra do WhatsApp.',
    acao: 'Avise o administrador.',
  },
  132012: {
    causa: 'conteudo',
    rotulo: 'Variável em formato inválido',
    explicacao: 'Uma variável ou a mídia do cabeçalho não está no formato do template.',
    acao: 'Confira as variáveis e a mídia e envie de novo.',
  },
  /* A Meta dá a causa exata no `error_data.details` (disparo e0b7494e da bancada, 2026-10-26). O envio já
     limpa as variáveis (back `template_params.py`); o que sobra aqui é o caso que a limpeza não cobre. */
  132018: {
    causa: 'conteudo',
    rotulo: 'Variável com quebra ou espaços',
    explicacao: 'Uma variável do template tem quebra de linha, tab ou mais de 4 espaços seguidos.',
    acao: 'Corrija a variável e envie de novo.',
  },
  132015: {
    causa: 'conteudo',
    rotulo: 'Template pausado',
    explicacao: 'O WhatsApp pausou o template por baixa qualidade.',
    acao: 'Use outro template ou avise o administrador.',
  },
  132016: {
    causa: 'conteudo',
    rotulo: 'Template desativado',
    explicacao: 'O WhatsApp desativou este template de vez.',
    acao: 'Use outro template.',
  },
  131064: {
    causa: 'conteudo',
    rotulo: 'Templates limitados por categoria',
    explicacao: 'O WhatsApp limitou os envios porque há templates na categoria errada.',
    acao: 'Avise o administrador.',
  },
  // — conta —
  131063: {
    causa: 'conta',
    rotulo: 'Marketing desativado na conta',
    explicacao: 'Esta conta está configurada para não enviar marketing.',
    acao: 'Avise o administrador.',
  },
  131048: {
    causa: 'conta',
    rotulo: 'Número limitado por qualidade',
    explicacao: 'O WhatsApp restringiu este número por denúncias ou baixa qualidade.',
    acao: 'Avise o administrador.',
  },
  131042: {
    causa: 'conta',
    rotulo: 'Pagamento pendente na conta',
    explicacao: 'A conta do WhatsApp está com problema no pagamento.',
    acao: 'Avise o administrador.',
  },
  368: {
    causa: 'conta',
    rotulo: 'Conta restrita pelo WhatsApp',
    explicacao: 'A conta foi restrita por violar uma política do WhatsApp.',
    acao: 'Avise o administrador.',
  },
  131031: {
    causa: 'conta',
    rotulo: 'Conta bloqueada pelo WhatsApp',
    explicacao: 'O WhatsApp bloqueou ou restringiu esta conta.',
    acao: 'Avise o administrador.',
  },
  130497: {
    causa: 'conta',
    rotulo: 'País do contato não permitido',
    explicacao: 'Esta conta não pode enviar para números do país deste contato.',
    acao: 'Avise o administrador.',
  },
  // — limite —
  130429: {
    causa: 'limite',
    rotulo: 'Muitos envios ao mesmo tempo',
    explicacao: 'O WhatsApp recusou parte dos envios por excesso de mensagens por segundo.',
    acao: 'Tente de novo em alguns minutos.',
  },
  131056: {
    causa: 'limite',
    rotulo: 'Muitas mensagens para este contato',
    explicacao: 'Foram enviadas várias mensagens seguidas para o mesmo contato.',
    acao: 'Aguarde alguns segundos e envie de novo.',
  },
  // — meta —
  131057: {
    causa: 'meta',
    rotulo: 'Conta em manutenção',
    explicacao: 'A conta estava em manutenção no WhatsApp, o que leva até 1 minuto.',
    acao: 'Tente de novo em instantes.',
  },
  131000: {
    causa: 'meta',
    rotulo: 'Erro do WhatsApp',
    explicacao: 'O WhatsApp não conseguiu enviar e não informou o motivo.',
    acao: 'Tente de novo.',
  },
  131016: {
    causa: 'meta',
    rotulo: 'WhatsApp fora do ar',
    explicacao: 'O serviço do WhatsApp estava indisponível no momento do envio.',
    acao: 'Tente de novo mais tarde.',
  },
  // — integração —
  100: {
    causa: 'integracao',
    rotulo: 'Erro no sistema',
    explicacao: 'Nosso sistema montou o envio com um dado inválido. Não é problema do contato.',
    acao: 'Avise o suporte com o código do erro.',
  },
  131009: {
    causa: 'integracao',
    rotulo: 'Erro no sistema',
    explicacao: 'Nosso sistema montou o envio com um dado inválido. Não é problema do contato.',
    acao: 'Avise o suporte com o código do erro.',
  },
}

/** Texto quando o código não tem copy própria mas a causa é conhecida. */
export const COPY_DA_CAUSA: Record<CausaDoErroMeta, CopyDoErroMeta> = {
  contato: {
    rotulo: 'Não entregue ao contato',
    explicacao: 'O motivo está do lado do contato: número, preferências ou limites dele.',
  },
  conteudo: {
    rotulo: 'Mensagem recusada pelo WhatsApp',
    explicacao: 'O WhatsApp recusou o conteúdo enviado.',
    acao: 'Revise a mensagem antes de reenviar.',
  },
  conta: {
    rotulo: 'Problema na conta do WhatsApp',
    explicacao: 'Uma configuração da conta ou do número impediu o envio.',
    acao: 'Avise o administrador.',
  },
  limite: {
    rotulo: 'Limite de envio atingido',
    explicacao: 'O WhatsApp limitou a quantidade de envios.',
    acao: 'Tente de novo mais tarde.',
  },
  meta: {
    rotulo: 'Erro do WhatsApp',
    explicacao: 'O WhatsApp não conseguiu processar o envio.',
    acao: 'Tente de novo.',
  },
  integracao: {
    rotulo: 'Erro no sistema',
    explicacao: 'Nosso sistema montou o envio com um dado inválido.',
    acao: 'Avise o suporte com o código do erro.',
  },
}

/** Códigos sem copy própria cuja causa a auditoria fixa (o chat não recebe causa do back). */
const CAUSA_SEM_COPY: Record<number, CausaDoErroMeta> = {
  131021: 'contato',
  131052: 'contato',
  132005: 'conteudo',
  132068: 'conteudo',
  132069: 'conteudo',
  0: 'conta',
  190: 'conta',
  3: 'conta',
  10: 'conta',
  33: 'conta',
  131005: 'conta',
  131037: 'conta',
  131045: 'conta',
  134011: 'conta',
  4: 'limite',
  80007: 'limite',
  1: 'meta',
  2: 'meta',
  131060: 'meta',
  133004: 'meta',
  131008: 'integracao',
  135000: 'integracao',
}

/** Causa pelo código — tabela do front. `undefined` = fora do catálogo (o genérico decide, não um chute). */
export function causaDoCodigo(codigo: number): CausaDoErroMeta | undefined {
  const explicita = COPY_DO_CODIGO[codigo]?.causa ?? CAUSA_SEM_COPY[codigo]
  if (explicita) return explicita
  if (codigo >= 200 && codigo <= 299) return 'conta'
  if (codigo >= 132000 && codigo <= 132099) return 'conteudo'
  if (codigo >= 133000 && codigo <= 133016) return 'conta'
  return undefined
}

/** `ausente` = back velho ou sem causa (o front deriva pelo código); `desconhecida` = valor que o front não
 *  conhece — vai pro genérico, sem derivar: o back sabe algo que o front não sabe, e chutar dá conselho errado. */
function lerCausa(causa: string | null | undefined): CausaDoErroMeta | 'ausente' | 'desconhecida' {
  if (typeof causa !== 'string' || causa.trim() === '') return 'ausente'
  return (CAUSAS as readonly string[]).includes(causa) ? (causa as CausaDoErroMeta) : 'desconhecida'
}

export interface EntradaDoMotivo {
  codigo?: number | null
  /** `error_cause` / `skip_detail.cause` / `meta_cause` do back. `undefined`/`null` = back velho ou sem código. */
  causa?: string | null
}

export function motivoDaFalha({ codigo: codigoCru, causa: causaCrua }: EntradaDoMotivo): MotivoDaFalha {
  const codigo = typeof codigoCru === 'number' && Number.isFinite(codigoCru) ? codigoCru : undefined
  const comCodigo = codigo === undefined ? {} : { codigo }

  const propria = codigo === undefined ? undefined : COPY_DO_CODIGO[codigo]
  if (propria) {
    const { causa, ...copia } = propria
    return { ...copia, causa, ...comCodigo, origem: 'codigo' }
  }

  const lida = lerCausa(causaCrua)
  const causa =
    lida === 'desconhecida' ? undefined : lida === 'ausente' ? (codigo === undefined ? undefined : causaDoCodigo(codigo)) : lida
  if (causa) return { ...COPY_DA_CAUSA[causa], causa, ...comCodigo, origem: 'causa' }

  return { ...COPY_GENERICA, ...comCodigo, origem: 'generico' }
}
